import React, {useState} from 'react';
import {FormControl, Select, MenuItem, Typography, Box, Divider} from '@mui/material';
import api from '../../../../api/axios';
import {toast} from 'react-toastify';
import BaseModal from '../../../utils/BaseModal';

// modal handles explicit target entities mapped to JPA architecture
const TradeActionModal = ({open, mode, entityId, entityType, selectedIds, onClose, onSuccess}: any) => {
    const [actionType, setActionType] = useState('CANCEL_AND_PENALIZE');
    const [targetUser, setTargetUser] = useState('SELLER');

    const handleExecute = async () => {
        const ids = mode === 'SINGLE' ? [entityId] : selectedIds;
        try {
            await toast.promise(
                api.post('/system/trades/manage-action', {
                    targetIds: ids,
                    entityType: entityType,
                    action: actionType,
                    penaltyTargetUser: targetUser,
                    revertPenalty: actionType === 'REVERT_PENALTY'
                }),
                { pending: 'Processing system action...', success: 'Action executed successfully', error: 'Failed to process action' }
            );
            onSuccess();
            onClose();
        } catch (e) { console.error(e); }
    };

    const getEntityDisplayLabel = () => {
        if (entityType === 'TRADE') return 'Single Trade';
        if (entityType === 'TRADE_GROUP') return 'Trade Group';
        if (entityType === 'BUY_GROUP') return 'Buy Group (Parent Order)';
        if (entityType === 'SELL_GROUP') return 'Sell Group (Parent Order)';
        return 'Entity';
    };

    return (
        <BaseModal
            open={open}
            title={mode === 'SINGLE' ? `System Intervention: ${getEntityDisplayLabel()} #${entityId}` : `Batch Action (${selectedIds.length} ${getEntityDisplayLabel()}s)`}
            onAbort={onClose}
            onSubmit={handleExecute}
            submitText="Execute System Action"
            submitColor="error"
            abortText="Cancel"
            maxWidth="sm"
            mfaMode="REQUIRED"
        >
            <div className="flex flex-col gap-5 pt-4">

                <Box className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-lg border border-blue-100 dark:border-blue-500/20">
                    <Typography className="text-[10px] font-black text-blue-700 dark:text-blue-400 uppercase tracking-widest mb-1">
                        Active Scope Context
                    </Typography>
                    <Typography className="text-[13px] font-bold text-gray-900 dark:text-gray-100 mb-2">
                        Applying changes to: <span className="text-blue-600 dark:text-blue-400 underline">{getEntityDisplayLabel()}</span>
                    </Typography>
                    <Typography className="text-[10px] text-gray-500 font-medium leading-tight">
                        The backend will apply this action natively to the corresponding {entityType} entity and cascade state changes, penalties, and fee adjustments to underlying ledger transactions and entries.
                    </Typography>
                </Box>

                <Divider className="dark:!border-white/10" />

                <Box>
                    <Typography className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1">
                        Select Operation
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={actionType} onChange={(e) => setActionType(e.target.value)}
                            className="bg-gray-50 dark:bg-neutral-800/50 rounded"
                            sx={{fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(150,150,150,0.2)' }}}
                        >
                            <MenuItem value="CANCEL_AND_PENALIZE" className="text-xs uppercase font-bold text-red-500">Cancel & Penalize (Apply Penalty / Fee Adjustments)</MenuItem>
                            <MenuItem value="CANCEL_NO_PENALTY" className="text-xs uppercase font-bold">Cancel (No Penalty)</MenuItem>
                            <MenuItem value="REVERT_PENALTY" className="text-xs uppercase font-bold text-emerald-500">Revert Penalty / Refund (Remove Penalty)</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {actionType !== 'REVERT_PENALTY' && (
                    <Box>
                        <Typography className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5 ml-1">
                            Penalty Target User
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={targetUser} onChange={(e) => setTargetUser(e.target.value)}
                                className="bg-gray-50 dark:bg-neutral-800/50 rounded"
                                sx={{fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(150,150,150,0.2)' }}}
                            >
                                <MenuItem value="SELLER" className="text-xs uppercase font-bold">Seller</MenuItem>
                                <MenuItem value="BUYER" className="text-xs uppercase font-bold">Buyer</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                )}

                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex gap-3 items-start mt-2">
                    <span className="text-xl">⚠️</span>
                    <p className="text-[11px] text-red-600 dark:text-red-400 font-bold leading-tight">
                        Executing this will alter the ledger entries, fees, and penalties of the selected {getEntityDisplayLabel()} in the database. Penalties and reversals will directly update user balances.
                    </p>
                </div>
            </div>
        </BaseModal>
    );
};

export default TradeActionModal;